import { useLayoutEffect, useMemo, type FC } from 'react';
import { z } from 'zod';
import type { Link } from 'src/apis/api-utilities';
import { useTsos } from 'src/apis/transport-auction/use-tsos';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useSearch } from 'src/hooks/use-search';

export const tsoOptionSchema = formOptionSchema.merge(
  z.object({ hint: z.string() })
);

type TsoOption = z.input<typeof tsoOptionSchema>;

type Props = {
  link: Link;
  name: string;
  label: string;
  placeholder: string;
};

export const TsosFilter: FC<Props> = ({ link, name, label, placeholder }) => {
  const [searchValue, setSearchValue, searchQuery] = useSearch('', {
    minLength: 1, // backend needs null, instead of empty string to return all results
    delay: 100,
  });

  const tsos = useTsos();

  useLayoutEffect(() => {
    tsos.execute({ link, query: searchQuery });
    // oxlint-disable-next-line exhaustive-deps
  }, [link, searchQuery]);

  const searchResult = useMemo(() => {
    if (!tsos.response) return;

    const searchResult: TsoOption[] = tsos.response.data._embedded.items.map(
      (tso) => ({
        value: tso.id,
        label: tso.name,
        hint: tso.shortName,
      })
    );

    return searchResult;
  }, [tsos.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      placeholder={placeholder}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      options={searchResult}
      pending={tsos.pending}
      searchValueForCurrentOptions={tsos.response?.config.params.search}
    />
  );
};
